import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, Checkbox, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { COMPETENCY_PERMS as P, useCompetencyCan, useEffectivenessList } from '@/hooks/useCompetency';
import type { EffectivenessEvaluation } from '@/services/competency.service';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, STATUS_COLOR } from './competencyDisplay';

const EffectivenessListPage: React.FC = () => {
  const navigate = useNavigate();
  const canList = useCompetencyCan(P.trainingView, P.view, P.effectivenessEvaluate, P.trainingManage);
  const [status, setStatus] = useState<string | undefined>('Open');
  const [overdue, setOverdue] = useState(false);
  const [search, setSearch] = useState('');
  const { data = [], isLoading } = useEffectivenessList({ status, overdue: overdue ? '1' : undefined, search }, canList);

  const columns: ColumnsType<EffectivenessEvaluation> = [
    {
      title: 'Session',
      dataIndex: 'session_number',
      width: 150,
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/effectiveness/${r.id}`)}>{v}</a>,
    },
    { title: 'Training date', dataIndex: 'session_date', width: 120, render: (v) => formatDate(v) },
    { title: 'Training', render: (_, r) => `${r.course_code ?? ''} ${r.course_title}`.trim() },
    { title: 'Trainer', dataIndex: 'trainer_display', width: 170 },
    { title: 'Trainees', width: 100, align: 'right', render: (_, r) => `${r.scored_count ?? 0} / ${r.trainee_count ?? 0}` },
    {
      title: 'Due',
      dataIndex: 'due_date',
      width: 130,
      render: (v, r) => (
        <Space size={4}>
          {formatDate(v)}
          {r.overdue && <Tag color="red">Overdue</Tag>}
        </Space>
      ),
    },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: 'Effective',
      dataIndex: 'overall_effective',
      width: 90,
      render: (v, r) => (r.status === 'Open' ? '-' : Number(v) ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav title="Training effectiveness" />
      {!canList && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Evaluations assigned to you as line manager or trainer open from your notifications and task list."
        />
      )}
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search session, course or trainer" allowClear style={{ width: 280 }} onSearch={setSearch} />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={['Open', 'Completed', 'Acknowledged'].map((s) => ({ value: s, label: s }))}
          />
          <Checkbox checked={overdue} onChange={(e) => setOverdue(e.target.checked)}>
            Overdue only
          </Checkbox>
        </Space>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} />
      </Card>
    </div>
  );
};

export default EffectivenessListPage;
